import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import { MaintenanceDeviceProvider } from '../features/device-maintenance/contexts/MaintenanceDevice.context';
import useIntegrationEvents from '../hooks/useIntegrationEvents';
import useIntegrationInit from '../hooks/useIntegrationInit';
import {
  selectDeviceTypeMap,
  selectIntegrationFetching,
  selectIntegrations,
  selectSystemCount,
  selectSystemTypeMap,
} from '../state/selectors/integrationSelectors';
import { setUpdatedSelectedDevice } from '../state/slices/deviceSlice';
import {
  fetchDeviceDataByTimestamps,
  setSelectedDevice as setSelectedDeviceThunk,
} from '../state/thunks';
import { DeviceContext } from './DeviceContext';

/**
 * Comprehensive DeviceContextProvider that wraps all integrations-related contexts
 *
 * Provides:
 * - DeviceContext: updatedSelectedDevice state for device selection/updates
 * - MaintenanceDeviceProvider: maintenanceGroups and selectedDevice state for maintenance features
 *
 * This consolidates context management for the entire integrations feature
 */
const DeviceContextProvider = ({ children }) => {
  // ALL hooks must be called before any conditional logic or early returns
  const { isAuthenticated } = useContext(AuthContext);
  const dispatch = useDispatch();
  const {
    data: connectedDevices = [],
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
  } = useGetGroupsQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const systemTypeMap = useSelector(selectSystemTypeMap);
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const systemCount = useSelector(selectSystemCount);
  const fetching = useSelector(selectIntegrationFetching);
  const dataStartTime = useSelector((state) => state.integration.dataStartTime);
  const dataEndTime = useSelector((state) => state.integration.dataEndTime);
  const updatedSelectedDevice = useSelector((state) => state.device.updatedSelectedDevice);
  const selectedDeviceIndex = useSelector((state) => state.device.selectedDeviceIndex);

  useIntegrationInit({ enabled: isAuthenticated });
  useIntegrationEvents({ enabled: isAuthenticated });

  const setSelectedDevice = useCallback(
    (index, id = -1) => {
      dispatch(setSelectedDeviceThunk({ index, id }));
    },
    [dispatch],
  );

  const triggerUpdatedSelectedDevice = useCallback(
    () => dispatch(setUpdatedSelectedDevice()),
    [dispatch],
  );

  const getDeviceData = useCallback(
    (startTime, endTime, checkAgainstNodeTsInstead = false) => {
      // If we already have a cached range that fully contains the requested range, avoid fetching and
      // use the locally cached data instead. This prevents overwriting a larger cache with a smaller range
      // and avoids unnecessary network requests.
      const CACHE_TOLERANCE_MS = 2 * 60 * 1000; // 2 minutes from edge to allow small time differences

      if (
        typeof dataStartTime === 'number' &&
        typeof dataEndTime === 'number' &&
        startTime >= dataStartTime - CACHE_TOLERANCE_MS &&
        endTime <= dataEndTime + CACHE_TOLERANCE_MS
      ) {
        return Promise.resolve({ cached: true });
      }

      return dispatch(
        fetchDeviceDataByTimestamps({ startTime, endTime, checkAgainstNodeTsInstead }),
      );
    },
    [dispatch, dataStartTime, dataEndTime],
  );

  const deviceDataHook = useMemo(
    () => ({
      fetching,
      deviceTypes,
      deviceTypeMap,
      systemTypeMap,
      integrations,
      connectedDevices,
      deviceGroups,
      systemCount,
      getDeviceData,
    }),
    [
      fetching,
      deviceTypes,
      deviceTypeMap,
      systemTypeMap,
      integrations,
      connectedDevices,
      deviceGroups,
      systemCount,
      getDeviceData,
    ],
  );

  const value = useMemo(
    () => ({
      updatedSelectedDevice,
      setUpdatedSelectedDevice: triggerUpdatedSelectedDevice,
      selectedDeviceIndex,
      setSelectedDevice,
      selectedDevice: selectedDeviceIndex,
      integrations,
      devices: connectedDevices,
      connectedDevices,
      deviceTypes,
      deviceTypeMap,
      systemTypeMap,
      deviceGroups,
      systemCount,
      fetching,
      fetchingData: fetching,
      setIntegrations: (value) => {
        console.trace('setIntegrations called:', value);
      },
      setDevices: (value) => {
        console.trace('setDevices called:', value);
      },
      setDeviceGroups: (value) => {
        console.trace('setDeviceGroups called:', value);
      },
      getDeviceData,
      getDevices: (value) => {
        console.trace('getDevices called:', value);
      },
      getDevice: (value) => {
        console.trace('getDevice called:', value);
      },
      setFetchingData: (value) => {
        console.trace('setFetchingData called:', value);
      },
      deviceDataHook,
    }),
    [
      updatedSelectedDevice,
      triggerUpdatedSelectedDevice,
      selectedDeviceIndex,
      setSelectedDevice,
      integrations,
      connectedDevices,
      deviceTypes,
      deviceTypeMap,
      systemTypeMap,
      deviceGroups,
      systemCount,
      fetching,
      getDeviceData,
      deviceDataHook,
    ],
  );

  // Now we can safely do conditional rendering after ALL hooks are called
  if (!isAuthenticated) {
    console.log('DeviceContextProvider: User not authenticated, skipping context provision.');
    return <>{children}</>;
  }

  return (
    <DeviceContext.Provider value={value}>
      <MaintenanceDeviceProvider>{children}</MaintenanceDeviceProvider>
    </DeviceContext.Provider>
  );
};

export default DeviceContextProvider;
